import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Button } from '@hqo/ui-atoms';
import { cn } from '@hqo/react-hooks';
import {
  ACTIVITY_FILTER_ORDER,
  ACTIVITY_TYPE_CONFIG,
  ActivityFilter,
} from 'app/shared/consts/activity';

interface ActivityFilterChipsProps {
  active: ActivityFilter;
  onChange: (filter: ActivityFilter) => void;
  className?: string;
}

const labelIdFor = (filter: ActivityFilter): string => (
  filter === 'all' ? 'activity.filter.all' : ACTIVITY_TYPE_CONFIG[filter].filterLabelId
);

interface FilterChipProps {
  filter: ActivityFilter;
  label: string;
  isActive: boolean;
  onSelect: (filter: ActivityFilter) => void;
}

const FilterChip = ({ filter, label, isActive, onSelect }: FilterChipProps): JSX.Element => {
  const handleClick = useCallback(() => onSelect(filter), [onSelect, filter]);
  return (
    <Button
      type="button"
      variant={isActive ? 'default' : 'outline'}
      size="sm"
      aria-pressed={isActive}
      onClick={handleClick}
      data-testid={`activity-filter-${filter}`}
      className={cn(
        // Match the mock chip exactly (ChipsV2): 13px / semibold / 7px 14px padding, flat. The mock
        // renders with line-height 1.5 (19.5px) → 35.5px tall; `leading-normal` (= 1.5) matches it.
        'h-auto shrink-0 rounded-full px-3.5 py-[7px] text-[13px] font-semibold leading-normal shadow-none',
        // Inactive: the mock's light border (#e4e7ea) + grey text (#697177), and a hover pinned to
        // the mock's own grey rather than the DS `hover:bg-accent`. Kept explicit now that `--accent`
        // is no longer remapped to the brand: accent is a near-white surface, and the mock's chips
        // want a visible grey on hover.
        !isActive && 'border-[#e4e7ea] text-[#697177] hover:bg-system-grey-light hover:text-[#697177]',
      )}
    >
      {label}
    </Button>
  );
};

/**
 * Category filter — a horizontally-scrolling row of DS `Button` chips. The active chip is the
 * accent (`default` variant → tenant `--primary`); the rest are `outline`.
 */
export const ActivityFilterChips = ({ active, onChange, className }: ActivityFilterChipsProps): JSX.Element => {
  const intl = useIntl();

  return (
    <div
      /*
       * `overscroll-x-contain` because this is the ONE thing here that really scrolls sideways.
       * Everything else leaves the x axis free so the browser's back/forward swipe still works over
       * the miniapp; this rail has to opt back in, or swiping past its last chip would chain into a
       * history navigation instead of just stopping.
       */
      className={cn(
        'flex gap-2 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
      role="group"
      aria-label={intl.formatMessage({ id: 'activity.filter.aria' })}
      data-testid="activity-filter-chips"
    >
      {ACTIVITY_FILTER_ORDER.map((filter) => (
        <FilterChip
          key={filter}
          filter={filter}
          label={intl.formatMessage({ id: labelIdFor(filter) })}
          isActive={active === filter}
          onSelect={onChange}
        />
      ))}
    </div>
  );
};
